import type {
  FeedbackWidgetConfig,
  FeedbackSubmitResult,
  DEFAULT_CONFIG,
  ResolvedFeedbackConfig,
} from './types.js';
import { injectStyles } from './styles.js';
import { ICONS } from './icons.js';
import {
  ScreenshotManager,
  ScreenshotResult,
  ScreenshotCaptureState,
  ScreenshotProgress,
  ScreenshotCaptureMethod,
  isMobileDevice,
} from './screenshot.js';
import {
  validateImageFile,
  getAllowedMimeTypes,
  getFileInputAcceptAttribute,
  getAllowedFormatsText,
} from './imageValidation.js';

/** DOM id of the shared tooltip bubble, referenced via `aria-describedby`. */
const TOOLTIP_ID = 'kern-feedback-tooltip';

/**
 * Property under which a widget instance is tagged onto its own container
 * element. This makes a live widget discoverable across *separate copies of
 * the bundle* on the same page (a host that loads the script twice), where
 * module-level state and `instanceof` checks do not carry over.
 */
export const WIDGET_CONTAINER_PROPERTY = '__kernFeedbackWidget';

/**
 * KERN Feedback Widget
 *
 * Ein einbettbares Feedback-Widget im KERN Design System Stil.
 *
 * @example
 * ```js
 * const widget = new FeedbackWidget({
 *   apiKey: 'your-api-key',
 *   position: 'bottom-right',
 * });
 * widget.init();
 * ```
 */
export class FeedbackWidget {
  private config: ResolvedFeedbackConfig;
  private container: HTMLElement | null = null;
  private dialog: HTMLElement | null = null;
  private backdrop: HTMLElement | null = null;
  private isOpen = false;
  private isSubmitting = false;
  private currentRating = 0;
  private screenshot: ScreenshotResult | null = null;

  // Trigger buttons. The desktop button always lives inside `container` and
  // floats via CSS. The mobile button is either mounted inline into a host
  // container (config.triggerContainer) or falls back into `container` as a
  // fixed corner icon. References are kept so lifecycle code (screenshot
  // hide/show, theme sync, destroy) does not rely on where they are mounted.
  private desktopTriggerBtn: HTMLButtonElement | null = null;
  private mobileTriggerBtn: HTMLButtonElement | null = null;
  // The trigger element that opened the dialog, so focus can be restored on close.
  private lastTrigger: HTMLElement | null = null;

  // Async screenshot management
  private screenshotManager: ScreenshotManager;
  private screenshotStateUnsubscribe: (() => void) | null = null;
  private screenshotProgressUnsubscribe: (() => void) | null = null;
  private userWantsScreenshot = false; // User explicitly wants to attach screenshot

  // Document-ready handler for deferred auto-init (stored so destroy() can cancel it)
  private domReadyHandler: (() => void) | null = null;

  // Document-level Escape handler (stored so it can be removed in destroy())
  private escapeKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  // Theme synchronisation with the host page (KERN light/dark mode)
  private themeObserver: MutationObserver | null = null;
  private themeMediaQuery: MediaQueryList | null = null;
  private themeMediaListener: (() => void) | null = null;

  // Page-scroll lock state (saved before open, restored on close)
  private savedBodyOverflow: string | null = null;
  private savedHtmlOverflow: string | null = null;

  // Pending timers, tracked so destroy() can cancel them. Without this a
  // deferred callback (background screenshot start, auto-close after a
  // successful submit) could fire against an already-torn-down widget — the
  // classic SPA "unmount while async work is in flight" bug.
  private pendingTimers = new Set<ReturnType<typeof setTimeout>>();

  // In-flight submit request, aborted on destroy for the same reason.
  private submitAbortController: AbortController | null = null;

  // Shared tooltip bubble for icon-only action buttons (see `attachTooltip`).
  private tooltipEl: HTMLElement | null = null;
  private tooltipAnchor: HTMLElement | null = null;
  private tooltipDismissHandler: (() => void) | null = null;

  constructor(config: FeedbackWidgetConfig) {
    const defaults: typeof DEFAULT_CONFIG = {
      apiUrl: 'https://feedback.kern-ux.de/api/v1',
      position: 'bottom-right',
      primaryColor: '#004B76',
      buttonText: 'KERN Feedback',
      dialogTitle: 'Feedback geben',
      lightDarkMode: 'auto',
      commentPlaceholder: 'Deine Mitteilung an uns...',
      showEmail: true,
      showRating: true,
      enableScreenshot: true,
      debugScreenshot: false,
      simulateMobile: false,
      screenshotMethod: 'native',
      autoInit: true,
      zIndex: 999999,
    };

    this.config = {
      ...defaults,
      ...config,
      onSubmit: config.onSubmit,
      onError: config.onError,
      onOpen: config.onOpen,
      onClose: config.onClose,
    } as ResolvedFeedbackConfig;

    // Initialize screenshot manager
    this.screenshotManager = new ScreenshotManager();

    // Enable debug mode if configured
    if (this.config.debugScreenshot) {
      this.screenshotManager.setDebugMode(true);
    }

    // Set screenshot capture method
    if (this.config.screenshotMethod) {
      this.screenshotManager.setCaptureMethod(
        this.config.screenshotMethod as ScreenshotCaptureMethod
      );
    }

    if (this.config.autoInit && document.readyState === 'loading') {
      // Store the handler so destroy() can cancel a still-pending auto-init.
      this.domReadyHandler = () => this.init();
      document.addEventListener('DOMContentLoaded', this.domReadyHandler);
    } else if (this.config.autoInit) {
      this.init();
    }
  }

  /**
   * `setTimeout` that registers the handle so `destroy()` can cancel it, and
   * that skips the callback if the widget was torn down in the meantime.
   */ private schedule(callback: () => void, delay: number): void {
    const handle = setTimeout(() => {
      this.pendingTimers.delete(handle);
      callback();
    }, delay);
    this.pendingTimers.add(handle);
  }

  /** Cancel every pending timer registered via `schedule()`. */
  private clearPendingTimers(): void {
    for (const handle of this.pendingTimers) {
      clearTimeout(handle);
    }
    this.pendingTimers.clear();
  }

  /**
   * Create the single tooltip bubble shared by every icon-only action button.
   *
   * It lives inside the widget's body-level container (never inside the host's
   * header or the dialog) and is placed with `position: fixed`, so no host
   * `overflow`/`contain` rule can clip it and no transformed ancestor can trap
   * it. Design follows the KERN Chatbot widget: an inverted bubble that reads
   * dark on light pages and light on dark pages.
   */
  private createTooltip(): void {
    const tooltip = document.createElement('div');
    tooltip.className = 'kern-feedback-tooltip';
    tooltip.id = TOOLTIP_ID;
    tooltip.setAttribute('role', 'tooltip');
    tooltip.setAttribute('data-kern-theme', this.detectHostTheme());
    this.tooltipEl = tooltip;
    this.container?.appendChild(tooltip);

    // A fixed bubble would drift away from its anchor once the page scrolls or
    // the viewport changes, so dismiss it instead of trying to track.
    this.tooltipDismissHandler = () => this.hideTooltip();
    window.addEventListener('scroll', this.tooltipDismissHandler, true);
    window.addEventListener('resize', this.tooltipDismissHandler);
  }

  /**
   * Show `label` while `trigger` is hovered or keyboard-focused.
   *
   * @param placement Preferred side. Use `'bottom'` for triggers that sit at
   * the top of the page (the header trigger), `'top'` otherwise. The bubble
   * flips automatically when the preferred side has no room.
   */
  private attachTooltip(
    trigger: HTMLElement,
    label: string,
    placement: 'top' | 'bottom' = 'top'
  ): void {
    const show = () => this.showTooltip(trigger, label, placement);
    const hide = () => this.hideTooltip();

    trigger.addEventListener('mouseenter', show);
    trigger.addEventListener('mouseleave', hide);
    // Keyboard users get the tooltip too; a mouse click that moves focus onto
    // the button should not leave it hanging, hence the `:focus-visible` test.
    trigger.addEventListener('focus', () => {
      let keyboard = true;
      try {
        keyboard = trigger.matches(':focus-visible');
      } catch {
        // :focus-visible unsupported (older engines, jsdom) — show anyway.
      }
      if (keyboard) show();
    });
    trigger.addEventListener('blur', hide);
    trigger.addEventListener('click', hide);
  }

  /** Position and reveal the shared bubble next to `trigger`. */
  private showTooltip(trigger: HTMLElement, label: string, placement: 'top' | 'bottom'): void {
    const tooltip = this.tooltipEl;
    if (!tooltip || !label) return;

    tooltip.textContent = label;
    tooltip.setAttribute('data-kern-theme', this.detectHostTheme());
    tooltip.classList.add('kern-feedback-tooltip--visible');

    const gap = 8;
    const anchor = trigger.getBoundingClientRect();
    // Measured after the text was set, so the box reflects the final label.
    const bubble = tooltip.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 0;
    const viewportWidth = window.innerWidth || 0;

    const above = anchor.top - bubble.height - gap;
    const below = anchor.bottom + gap;
    let top = placement === 'bottom' ? below : above;
    // Flip to the other side when the preferred one would leave the viewport.
    if (placement === 'top' && above < gap) {
      top = below;
    } else if (placement === 'bottom' && below + bubble.height > viewportHeight - gap) {
      top = Math.max(gap, above);
    }

    let left = anchor.left + anchor.width / 2 - bubble.width / 2;
    // Keep the bubble fully on screen (matters for a trigger in a page corner).
    left = Math.max(gap, Math.min(left, Math.max(gap, viewportWidth - bubble.width - gap)));

    tooltip.style.top = `${Math.round(top)}px`;
    tooltip.style.left = `${Math.round(left)}px`;

    trigger.setAttribute('aria-describedby', TOOLTIP_ID);
    this.tooltipAnchor = trigger;
  }

  /** Hide the shared bubble and unlink it from its current anchor. */
  private hideTooltip(): void {
    this.tooltipEl?.classList.remove('kern-feedback-tooltip--visible');
    this.tooltipAnchor?.removeAttribute('aria-describedby');
    this.tooltipAnchor = null;
  }

  /**
   * Give every icon-only action button a tooltip. Buttons that already carry a
   * visible text label (the desktop float, "Absenden", the screenshot capture
   * button) are deliberately left alone — a tooltip there would just repeat
   * what is already on screen.
   */
  private attachActionTooltips(): void {
    // Header trigger: opens downwards because it sits at the top of the page.
    if (this.mobileTriggerBtn) {
      this.attachTooltip(this.mobileTriggerBtn, this.config.buttonText, 'bottom');
    }

    const closeButton = this.dialog?.querySelector('#kern-feedback-close');
    if (closeButton instanceof HTMLElement) {
      this.attachTooltip(closeButton, closeButton.getAttribute('aria-label') ?? '', 'bottom');
    }

    // Both screenshot variants (captured preview and uploaded file preview)
    // render a remove button; the dialog HTML contains only one at a time.
    for (const remove of Array.from(
      this.dialog?.querySelectorAll('.kern-feedback-screenshot__remove') ?? []
    )) {
      if (remove instanceof HTMLElement) {
        this.attachTooltip(remove, remove.getAttribute('aria-label') ?? '', 'top');
      }
    }
  }

  /**
   * Check if mobile behavior should be used.
   * Returns true if on actual mobile device OR if simulateMobile is enabled.
   */
  private isMobileBehavior(): boolean {
    return this.config.simulateMobile || isMobileDevice();
  }

  /**
   * Detect Safari browsers (desktop and iOS Safari) while excluding other
   * WebKit-based browsers such as Chrome, Edge, Firefox, and Opera on iOS/macOS.
   */
  private isSafariBrowser(): boolean {
    const userAgent = navigator.userAgent;
    const vendor = navigator.vendor;

    const isSafariEngine = /Safari/i.test(userAgent) && /Apple/i.test(vendor);
    const isOtherAppleWebKitBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/i.test(userAgent);
    const isChromeOrChromiumDesktop = /Chrome|Chromium|Edg|OPR/i.test(userAgent);

    return isSafariEngine && !isOtherAppleWebKitBrowser && !isChromeOrChromiumDesktop;
  }

  /**
   * Determine the host page's active KERN color mode.
   *
   * Detection order (matches KERN's own resolution):
   *   0. An explicit `lightDarkMode: 'light' | 'dark'` config option — when
   *      set, this pins the theme and skips all host detection below.
   *   1. An explicit `data-kern-theme="light|dark"` on `<html>` or `<body>`.
   *   2. A KERN utility class `.kern-light` / `.kern-dark` on `<html>` or `<body>`.
   *   3. The operating system preference (`prefers-color-scheme`).
   *   4. Fallback to `light`.
   *
   * Steps 1-4 only apply when `lightDarkMode` is `'auto'` (the default).
   */
  private detectHostTheme(): 'light' | 'dark' {
    // 0. Explicit config override — disables auto-detection entirely.
    if (this.config.lightDarkMode === 'light' || this.config.lightDarkMode === 'dark') {
      return this.config.lightDarkMode;
    }

    try {
      const roots = [document.documentElement, document.body].filter(
        (el): el is HTMLElement => !!el
      );

      // 1. Explicit data-kern-theme attribute
      for (const el of roots) {
        const attr = el.getAttribute('data-kern-theme');
        if (attr === 'dark' || attr === 'light') {
          return attr;
        }
      }

      // 2. KERN utility classes
      for (const el of roots) {
        if (el.classList.contains('kern-dark')) return 'dark';
        if (el.classList.contains('kern-light')) return 'light';
      }

      // 3. System preference
      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
          return 'dark';
        }
      }
    } catch {
      // Non-DOM / SSR environments — fall through to the default.
    }

    return 'light';
  }

  /**
   * Re-read the host theme and apply it to the widget so all KERN components
   * switch colors. The dialog/backdrop/desktop trigger live inside `container`;
   * the mobile trigger may be mounted into a host container (e.g. the site
   * header) outside `container`, so it carries its own `data-kern-theme` and is
   * updated independently here.
   */
  private applyHostTheme(): void {
    const theme = this.detectHostTheme();
    if (this.container && this.container.getAttribute('data-kern-theme') !== theme) {
      this.container.setAttribute('data-kern-theme', theme);
    }
    if (this.mobileTriggerBtn && this.mobileTriggerBtn.getAttribute('data-kern-theme') !== theme) {
      this.mobileTriggerBtn.setAttribute('data-kern-theme', theme);
    }
    if (this.tooltipEl && this.tooltipEl.getAttribute('data-kern-theme') !== theme) {
      this.tooltipEl.setAttribute('data-kern-theme', theme);
    }
  }

  /**
   * Watch the host page for color-mode changes and keep the widget in sync:
   *   - a MutationObserver reacts to `data-kern-theme` / class toggles on
   *     `<html>` and `<body>` (e.g. a theme switch button),
   *   - a media-query listener reacts to OS preference changes when the host
   *     has not pinned an explicit theme.
   *
   * Skipped entirely when `lightDarkMode` is set to `'light'` or `'dark'`,
   * since the theme is pinned and cannot change at runtime.
   */
  private setupThemeSync(): void {
    if (this.config.lightDarkMode === 'light' || this.config.lightDarkMode === 'dark') {
      return;
    }

    const observeOptions: MutationObserverInit = {
      attributes: true,
      attributeFilter: ['data-kern-theme', 'class'],
    };

    this.themeObserver = new MutationObserver(() => this.applyHostTheme());
    this.themeObserver.observe(document.documentElement, observeOptions);
    if (document.body) {
      this.themeObserver.observe(document.body, observeOptions);
    }

    try {
      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        this.themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.themeMediaListener = () => this.applyHostTheme();
        if (typeof this.themeMediaQuery.addEventListener === 'function') {
          this.themeMediaQuery.addEventListener('change', this.themeMediaListener);
        } else if (typeof this.themeMediaQuery.addListener === 'function') {
          // Safari < 14 fallback
          this.themeMediaQuery.addListener(this.themeMediaListener);
        }
      }
    } catch {
      // matchMedia unavailable (e.g. jsdom) — static theme detection only.
    }
  }

  /**
   * Whether `init()` has run and the widget is currently in the DOM.
   *
   * The mount registry (`mount.ts`) uses this to detect a widget that was torn
   * down through `widget.destroy()` or the `window.KernFeedback.destroy()`
   * shortcut instead of `mount.unmount()`, so the slot can be reused.
   */
  public get isInitialized(): boolean {
    return this.container !== null;
  }

  /**
   * Initialisiert das Widget
   */
  public init(): void {
    if (this.container) {
      console.warn('KERN Feedback Widget bereits initialisiert');
      return;
    }

    // Styles injizieren
    injectStyles();

    // Container erstellen
    this.container = document.createElement('div');
    this.container.className = 'kern-feedback-widget';
    // Mirror the host page's KERN color mode (light/dark) so the widget's
    // floating button, mobile trigger and dialog use the correct KERN tokens.
    this.container.setAttribute('data-kern-theme', this.detectHostTheme());
    this.container.style.setProperty('--kern-z-index', String(this.config.zIndex));
    this.container.style.setProperty('--kern-primary', this.config.primaryColor);
    // Tag the instance onto its container so a second copy of the bundle on the
    // same page can find the live widget instead of building a duplicate.
    Object.defineProperty(this.container, WIDGET_CONTAINER_PROPERTY, {
      value: this,
      configurable: true,
      enumerable: false,
      writable: true,
    });

    // Trigger-Buttons erstellen (Desktop-Float + Mobile-Inline/Fallback)
    this.createTriggerButton();

    // Dialog erstellen
    this.createDialog();

    // Shared tooltip bubble for the icon-only action buttons. Created after the
    // triggers/dialog so it is the last child and therefore paints above them.
    this.createTooltip();
    this.attachActionTooltips();

    // Zum DOM hinzufügen
    document.body.appendChild(this.container);

    // Keep the widget's color mode in sync with the host page
    this.setupThemeSync();

    // Log simulateMobile mode if enabled
    if (this.config.simulateMobile) {
      console.log(
        '📱 KERN Feedback: simulateMobile mode enabled - showing mobile file upload UI instead of native screenshot capture'
      );
    }
  }

  /**
   * Öffnet den Feedback-Dialog
   */
  public open(): void {
    if (this.isOpen || !this.dialog || !this.backdrop) return;

    this.isOpen = true;

    // Lock background page scroll while the modal is open. Without this the
    // page underneath keeps its scrollbar visible side-by-side with the
    // dialog body's own scrollbar — the classic "two scrollbars" bug — and
    // wheel/touch events leak through to the page behind the backdrop.
    this.lockPageScroll();

    // Show dialog immediately - no waiting for screenshot
    this.backdrop.classList.add('kern-feedback-backdrop--visible');
    this.dialog.classList.add('kern-dialog--visible');
    this.dialog.setAttribute('open', '');

    // Focus auf erstes Eingabefeld
    const firstInput = this.dialog.querySelector('textarea, input') as HTMLElement;
    firstInput?.focus();

    this.config.onOpen?.();

    // Start screenshot capture in background AFTER dialog is visible
    // Use setTimeout to ensure dialog is fully rendered first
    // Note: Native method requires user interaction, so we skip background capture
    // and only capture when user clicks the "Attach screenshot" button
    if (this.config.enableScreenshot && this.config.screenshotMethod !== 'native') {
      this.schedule(() => {
        this.startBackgroundScreenshotCapture();
      }, 50);
    }
  }

  /**
   * Save the current document/body overflow and force them to `hidden` so the
   * page underneath the modal can't be scrolled. Safe to call multiple times;
   * a second call is a no-op because savedBodyOverflow is already non-null.
   */
  private lockPageScroll(): void {
    if (this.savedBodyOverflow !== null) return;
    try {
      this.savedBodyOverflow = document.body.style.overflow;
      this.savedHtmlOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } catch {
      // SSR / non-DOM environments — nothing to lock.
    }
  }

  /**
   * Restore the original document/body overflow values captured by
   * lockPageScroll. Safe to call when no lock is currently held.
   */
  private unlockPageScroll(): void {
    if (this.savedBodyOverflow === null) return;
    try {
      document.body.style.overflow = this.savedBodyOverflow;
      document.documentElement.style.overflow = this.savedHtmlOverflow ?? '';
    } catch {
      // ignore — restore is best-effort
    } finally {
      this.savedBodyOverflow = null;
      this.savedHtmlOverflow = null;
    }
  }

  /**
   * Start background screenshot capture when dialog opens
   * The screenshot is captured immediately in the background.
   * If the user decides to attach it later, it will already be ready.
   */
  private startBackgroundScreenshotCapture(): void {
    // Subscribe to state changes to update UI
    this.screenshotStateUnsubscribe = this.screenshotManager.onStateChange((state, result, error) =>
      this.handleScreenshotStateChange(state, result, error)
    );

    // Subscribe to progress updates
    this.screenshotProgressUnsubscribe = this.screenshotManager.onProgress((progress) =>
      this.handleScreenshotProgress(progress)
    );

    // Update UI to show capturing state with progress
    this.updateScreenshotUI('capturing');

    // Start capture - widget elements are filtered out during capture, not hidden
    this.screenshotManager.startCapture();
  }

  /**
   * Handle screenshot progress updates from the manager
   */
  private handleScreenshotProgress(progress: ScreenshotProgress): void {
    const progressBar = this.dialog?.querySelector(
      '#kern-feedback-progress-bar'
    ) as HTMLProgressElement;
    const progressLabel = this.dialog?.querySelector(
      '#kern-feedback-progress-label'
    ) as HTMLElement;
    const progressContainer = this.dialog?.querySelector(
      '#kern-feedback-screenshot-progress'
    ) as HTMLElement;

    if (progressBar) {
      progressBar.value = progress.progress;
    }

    if (progressLabel) {
      progressLabel.textContent = progress.message;
    }

    // Show progress container during capture
    if (progressContainer && progress.progress < 100) {
      progressContainer.style.display = 'block';
    }
  }

  /**
   * Hide the widget (dialog, backdrop, trigger) before taking a screenshot
   * so they don't appear in the captured image
   */
  private hideWidgetForScreenshot(): void {
    if (this.dialog) {
      this.dialog.style.visibility = 'hidden';
    }
    if (this.backdrop) {
      this.backdrop.style.visibility = 'hidden';
    }
    // Also hide the trigger buttons (desktop and mobile). Use stored refs since
    // the mobile trigger may be mounted outside `container` (in the host header).
    if (this.desktopTriggerBtn) {
      this.desktopTriggerBtn.style.visibility = 'hidden';
    }
    if (this.mobileTriggerBtn) {
      this.mobileTriggerBtn.style.visibility = 'hidden';
    }
  }

  /**
   * Show the widget again after taking a screenshot
   */
  private showWidgetAfterScreenshot(): void {
    if (this.dialog) {
      this.dialog.style.visibility = '';
    }
    if (this.backdrop) {
      this.backdrop.style.visibility = '';
    }
    // Show the trigger buttons again (desktop and mobile).
    if (this.desktopTriggerBtn) {
      this.desktopTriggerBtn.style.visibility = '';
    }
    if (this.mobileTriggerBtn) {
      this.mobileTriggerBtn.style.visibility = '';
    }
  }

  /**
   * Handle screenshot state changes from the manager
   */
  private handleScreenshotStateChange(
    state: ScreenshotCaptureState,
    result?: ScreenshotResult,
    error?: Error
  ): void {
    this.updateScreenshotUI(state);

    if (state === 'completed' && result) {
      // Screenshot ready - if user already requested it AND nothing else is
      // attached yet, show the captured result. The `!this.screenshot` guard
      // prevents a late background capture from clobbering a screenshot the
      // user has already attached (e.g. via the desktop file upload).
      if (this.userWantsScreenshot && !this.screenshot) {
        this.screenshot = result;
        this.showScreenshotPreview(result);
      }
    } else if (state === 'failed' && error) {
      console.error('Background screenshot capture failed:', error);
      // Only notify user if they explicitly requested a screenshot
      if (this.userWantsScreenshot) {
        this.config.onError?.(error);
      }
    }
  }

  /**
   * Update the screenshot section UI based on capture state
   */
  private updateScreenshotUI(state: ScreenshotCaptureState): void {
    const captureBtn = this.dialog?.querySelector('#kern-feedback-capture') as HTMLButtonElement;
    const statusIndicator = this.dialog?.querySelector(
      '#kern-feedback-screenshot-status'
    ) as HTMLElement;
    const progressContainer = this.dialog?.querySelector(
      '#kern-feedback-screenshot-progress'
    ) as HTMLElement;

    if (!captureBtn) return;

    switch (state) {
      case 'capturing':
        // Show progress bar during capture
        if (progressContainer) {
          progressContainer.style.display = 'block';
        }
        // Hide the old status indicator text (replaced by progress bar)
        if (statusIndicator) {
          statusIndicator.style.display = 'none';
        }
        captureBtn.innerHTML = `<span class="kern-icon kern-icon--content-copy" aria-hidden="true"></span><span class="kern-label">Screenshot anhängen</span>`;
        captureBtn.disabled = false;
        break;

      case 'completed':
        // Hide progress bar when done
        if (progressContainer) {
          progressContainer.style.display = 'none';
        }
        // Screenshot ready - show as KERN badge with success status
        if (statusIndicator && !this.userWantsScreenshot) {
          statusIndicator.innerHTML = `<span class="kern-badge kern-badge--success"><span class="kern-icon kern-icon--success" aria-hidden="true"></span><span class="kern-label kern-label--small">Screenshot bereit</span></span>`;
          statusIndicator.style.display = 'flex';
        }
        captureBtn.innerHTML = `<span class="kern-icon kern-icon--content-copy" aria-hidden="true"></span><span class="kern-label">Screenshot anhängen</span>`;
        captureBtn.disabled = false;
        break;

      case 'failed':
        // Hide progress bar on failure
        if (progressContainer) {
          progressContainer.style.display = 'none';
        }
        // Capture failed - allow retry
        if (statusIndicator) {
          statusIndicator.innerHTML = `<span class="kern-body kern-body--small kern-body--muted">Screenshot konnte nicht erstellt werden</span>`;
          statusIndicator.style.display = 'flex';
        }
        captureBtn.innerHTML = `<span class="kern-icon kern-icon--content-copy" aria-hidden="true"></span><span class="kern-label">Erneut versuchen</span>`;
        captureBtn.disabled = false;
        break;

      case 'idle':
      case 'cancelled':
      default:
        // Hide progress bar
        if (progressContainer) {
          progressContainer.style.display = 'none';
        }
        if (statusIndicator) {
          statusIndicator.style.display = 'none';
        }
        captureBtn.innerHTML = `<span class="kern-icon kern-icon--content-copy" aria-hidden="true"></span><span class="kern-label">Screenshot erstellen</span>`;
        captureBtn.disabled = false;
        break;
    }
  }

  /**
   * Show the screenshot preview
   */
  private showScreenshotPreview(result: ScreenshotResult): void {
    const preview = this.dialog?.querySelector('#kern-feedback-preview') as HTMLElement;
    const previewImg = this.dialog?.querySelector('#kern-feedback-preview-img') as HTMLImageElement;
    const captureBtn = this.dialog?.querySelector('#kern-feedback-capture') as HTMLButtonElement;
    const statusIndicator = this.dialog?.querySelector(
      '#kern-feedback-screenshot-status'
    ) as HTMLElement;
    const progressContainer = this.dialog?.querySelector(
      '#kern-feedback-screenshot-progress'
    ) as HTMLElement;
    const fileWrapper = this.dialog?.querySelector(
      '#kern-feedback-file-wrapper'
    ) as HTMLElement | null;
    const separator = this.dialog?.querySelector(
      '#kern-feedback-screenshot-separator'
    ) as HTMLElement | null;
    const fileInput = this.dialog?.querySelector(
      '#kern-feedback-file-upload'
    ) as HTMLInputElement | null;

    if (preview && previewImg) {
      previewImg.src = result.dataUrl;
      preview.style.display = 'block';
    }

    if (captureBtn) {
      captureBtn.style.display = 'none';
    }

    if (statusIndicator) {
      statusIndicator.style.display = 'none';
    }

    if (progressContainer) {
      progressContainer.style.display = 'none';
    }

    // Hide the file-upload alternative (desktop) and the mobile file input
    // whenever a preview is shown, regardless of which path produced it.
    if (fileWrapper) {
      fileWrapper.style.display = 'none';
    }
    if (separator) {
      separator.style.display = 'none';
    }
    if (fileInput) {
      fileInput.style.display = 'none';
    }
  }

  /**
   * Schließt den Feedback-Dialog
   */
  public close(): void {
    if (!this.isOpen || !this.dialog || !this.backdrop) return;

    this.isOpen = false;
    this.backdrop.classList.remove('kern-feedback-backdrop--visible');
    this.dialog.classList.remove('kern-dialog--visible');
    this.dialog.removeAttribute('open');

    // Restore the underlying page's scrollbar / scroll position
    this.unlockPageScroll();

    // Cleanup screenshot manager subscriptions
    if (this.screenshotStateUnsubscribe) {
      this.screenshotStateUnsubscribe();
      this.screenshotStateUnsubscribe = null;
    }
    if (this.screenshotProgressUnsubscribe) {
      this.screenshotProgressUnsubscribe();
      this.screenshotProgressUnsubscribe = null;
    }
    this.screenshotManager.reset();
    this.userWantsScreenshot = false;

    // Formular zurücksetzen
    this.resetForm();

    this.config.onClose?.();

    // Return focus to the trigger that opened the dialog (keyboard/SR users),
    // provided it is still connected to the DOM.
    if (this.lastTrigger && this.lastTrigger.isConnected) {
      this.lastTrigger.focus();
    }
  }

  /**
   * Zerstört das Widget und entfernt es aus dem DOM
   */
  public destroy(): void {
    // Make sure we never leave the page in a locked-scroll state if the host
    // destroys the widget while the dialog is open.
    this.unlockPageScroll();

    // Cancel deferred work so no callback runs against a torn-down widget.
    this.clearPendingTimers();
    this.submitAbortController?.abort();
    this.submitAbortController = null;

    // Tear down the shared tooltip (the element itself goes with the container).
    this.hideTooltip();
    if (this.tooltipDismissHandler) {
      window.removeEventListener('scroll', this.tooltipDismissHandler, true);
      window.removeEventListener('resize', this.tooltipDismissHandler);
      this.tooltipDismissHandler = null;
    }
    this.tooltipEl = null;

    // Cleanup screenshot manager subscriptions
    if (this.screenshotStateUnsubscribe) {
      this.screenshotStateUnsubscribe();
      this.screenshotStateUnsubscribe = null;
    }
    if (this.screenshotProgressUnsubscribe) {
      this.screenshotProgressUnsubscribe();
      this.screenshotProgressUnsubscribe = null;
    }
    this.screenshotManager.reset();

    // Cancel a still-pending deferred auto-init (widget destroyed before the
    // document finished loading).
    if (this.domReadyHandler) {
      document.removeEventListener('DOMContentLoaded', this.domReadyHandler);
      this.domReadyHandler = null;
    }

    // Cleanup theme synchronisation
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
    if (this.themeMediaQuery && this.themeMediaListener) {
      if (typeof this.themeMediaQuery.removeEventListener === 'function') {
        this.themeMediaQuery.removeEventListener('change', this.themeMediaListener);
      } else if (typeof this.themeMediaQuery.removeListener === 'function') {
        this.themeMediaQuery.removeListener(this.themeMediaListener);
      }
    }
    this.themeMediaQuery = null;
    this.themeMediaListener = null;

    // Remove the document-level Escape handler added in setupDialogEvents.
    if (this.escapeKeyHandler) {
      document.removeEventListener('keydown', this.escapeKeyHandler);
      this.escapeKeyHandler = null;
    }

    // Remove the mobile trigger explicitly: it may have been mounted into a host
    // container (e.g. the site header) outside `container`, so removing the
    // container alone would leave it behind.
    this.mobileTriggerBtn?.remove();
    this.mobileTriggerBtn = null;
    this.desktopTriggerBtn = null;
    this.lastTrigger = null;

    this.container?.remove();
    this.container = null;
    this.dialog = null;
    this.backdrop = null;

    // Reset lifecycle state so a subsequent init() starts clean.
    this.isOpen = false;
    this.isSubmitting = false;
    this.currentRating = 0;
    this.screenshot = null;
    this.userWantsScreenshot = false;
  }

  /**
   * Resolve the configured `triggerContainer` (a CSS selector or an element)
   * into a concrete host element for the inline mobile trigger. Returns `null`
   * when none is configured, the selector is invalid/not found, or the value is
   * not an element — in which case the caller uses the fixed-corner fallback.
   */
  private resolveTriggerContainer(): HTMLElement | null {
    const target = this.config.triggerContainer;
    if (!target) return null;

    if (typeof target === 'string') {
      try {
        const el = document.querySelector(target);
        if (el instanceof HTMLElement) return el;
        console.warn(
          `KERN Feedback: triggerContainer "${target}" not found — using floating fallback trigger.`
        );
        return null;
      } catch {
        console.warn(
          `KERN Feedback: invalid triggerContainer selector "${target}" — using floating fallback trigger.`
        );
        return null;
      }
    }

    if (target instanceof HTMLElement) return target;

    console.warn(
      'KERN Feedback: triggerContainer must be a CSS selector or HTMLElement — using floating fallback trigger.'
    );
    return null;
  }

  private createTriggerButton(): void {
    const theme = this.detectHostTheme();

    // Desktop (≥575px): full floating button with text, anchored to the
    // viewport edge. Always lives inside `container` (in <body>) so its
    // `position: fixed` is relative to the viewport regardless of any host
    // header transforms.
    const desktopButton = document.createElement('button');
    desktopButton.className = `kern-btn kern-btn--primary kern-feedback-trigger kern-feedback-trigger--${this.config.position}`;
    desktopButton.setAttribute('aria-label', this.config.buttonText);
    desktopButton.setAttribute('aria-haspopup', 'dialog');
    desktopButton.setAttribute('type', 'button');
    desktopButton.innerHTML = `
      <span class="kern-icon kern-icon--mail kern-icon--default" aria-hidden="true"></span>
      <span class="kern-label">${this.config.buttonText}</span>
    `;
    desktopButton.addEventListener('click', () => {
      this.lastTrigger = desktopButton;
      this.open();
    });
    this.desktopTriggerBtn = desktopButton;
    this.container?.appendChild(desktopButton);

    // Mobile (≤574px): compact icon-only button. When a `triggerContainer` is
    // configured it is mounted INLINE (in normal flow) into that host element —
    // typically the site header — so it becomes part of the header layout with
    // no fixed positioning. Otherwise it falls back to a self-contained fixed
    // icon in the lower-right corner.
    const inlineHost = this.resolveTriggerContainer();
    const isInline = inlineHost !== null;

    const mobileButton = document.createElement('button');
    mobileButton.className = `kern-feedback-trigger-mobile kern-feedback-trigger-mobile--${
      isInline ? 'inline' : 'fallback'
    }`;
    // Carry the resolved KERN theme on the button itself so it renders with the
    // correct light/dark tokens even when mounted outside `container`.
    mobileButton.setAttribute('data-kern-theme', theme);
    mobileButton.setAttribute('aria-label', this.config.buttonText);
    mobileButton.setAttribute('aria-haspopup', 'dialog');
    mobileButton.setAttribute('type', 'button');
    // No native `title`: the shared tooltip bubble (see `attachTooltip`) is the
    // hover hint in both inline and fallback mode, and a `title` on top of it
    // would show a second, duplicated hint.
    mobileButton.innerHTML = `
      <span class="kern-feedback-trigger-mobile__icon" aria-hidden="true">${ICONS.feedback}</span>
    `;
    mobileButton.addEventListener('click', () => {
      this.lastTrigger = mobileButton;
      this.open();
    });
    this.mobileTriggerBtn = mobileButton;

    if (inlineHost) {
      inlineHost.appendChild(mobileButton);
    } else {
      this.container?.appendChild(mobileButton);
    }
  }

  private createDialog(): void {
    // Backdrop
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'kern-feedback-backdrop';
    this.backdrop.addEventListener('click', () => this.close());

    // Dialog (using native KERN dialog structure)
    this.dialog = document.createElement('dialog');
    this.dialog.className = 'kern-dialog';
    this.dialog.setAttribute('aria-labelledby', 'kern-feedback-title');
    this.dialog.setAttribute('aria-modal', 'true');

    this.dialog.innerHTML = this.getDialogHTML();

    // Event Listeners
    this.setupDialogEvents();

    this.container?.appendChild(this.backdrop);
    this.container?.appendChild(this.dialog);
  }

  private getDialogHTML(): string {
    const ratingSection = this.config.showRating
      ? `
        <div class="kern-form-input">
          <label class="kern-label">Wie bewertest du diese Seite?</label>
          <div class="kern-feedback-rating" role="radiogroup" aria-label="Bewertung">
            ${[1, 2, 3, 4, 5]
              .map(
                (n) => `
              <button type="button"
                      class="kern-feedback-rating__star"
                      data-rating="${n}"
                      role="radio"
                      aria-checked="false"
                      aria-label="${n} Stern${n > 1 ? 'e' : ''}">
                ${ICONS.starOutline}
              </button>
            `
              )
              .join('')}
            <span class="kern-feedback-rating__label kern-body" id="rating-label"></span>
          </div>
        </div>
      `
      : '';

    const emailSection = this.config.showEmail
      ? `
        <div class="kern-form-input" id="kern-feedback-email-field">
          <label class="kern-label" for="kern-feedback-email">
            E-Mail-Adresse
            <span class="kern-label__optional">- Optional</span>
          </label>
          <input class="kern-form-input__input"
                 type="email"
                 id="kern-feedback-email"
                 name="email"
                 autocomplete="email">
          <p class="kern-error" id="kern-feedback-email-error" role="alert" style="display: none;">
            <span class="kern-icon kern-icon--danger kern-icon--md" aria-hidden="true"></span>
            <span class="kern-body">Bitte gib eine gültige E-Mail-Adresse ein.</span>
          </p>
        </div>
      `
      : '';

    const screenshotSection = this.config.enableScreenshot
      ? this.isMobileBehavior()
        ? `
        <div class="kern-form-input" id="kern-feedback-file-wrapper">
          <label class="kern-label" for="kern-feedback-file-upload">Screenshot hochladen
            <span class="kern-label__optional">- Optional</span>
          </label>
          <div class="kern-hint" style="font-size: 12px; margin-top: 0.0rem; color: var(--kern-color-neutral-600, #6B7280);" id="kern-feedback-file-hint">Erlaubte Formate: ${getAllowedFormatsText()}. Maximale Dateigröße: 8 MB.
          Die automatische Screenshot-Funktion ist auf iOS und Android nicht verfügbar. Bitte erstelle einen Screenshot mit den Standardmethoden deines Betriebssystems und lade ihn hier hoch.</div>
          <input class="kern-form-input__input"
                 id="kern-feedback-file-upload"
                 name="screenshotFile"
                 type="file"
                 accept="${getFileInputAcceptAttribute()}"
                 aria-describedby="kern-feedback-file-hint">
          <div class="kern-feedback-screenshot__preview" id="kern-feedback-preview" style="display: none;">
            <img src="" alt="Screenshot Vorschau" id="kern-feedback-preview-img">
            <button type="button"
                    class="kern-feedback-screenshot__remove"
                    id="kern-feedback-remove-screenshot"
                    aria-label="Screenshot entfernen">
              ${ICONS.close}
            </button>
          </div>
        </div>
      `
        : `
        <div class="kern-form-input">
          <label class="kern-label">Screenshot</label>
          <div class="kern-feedback-screenshot">
            <button type="button" class="kern-btn kern-btn--secondary kern-btn--block" id="kern-feedback-capture">
              <span class="kern-icon kern-icon--content-copy" aria-hidden="true"></span>
              <span class="kern-label">Screenshot erstellen & anhängen</span>
            </button>
            <div class="kern-feedback-screenshot__progress" id="kern-feedback-screenshot-progress" style="display: none;">
              <div class="kern-progress">
                <label class="kern-label kern-label--small" for="kern-feedback-progress-bar" id="kern-feedback-progress-label">Screenshot wird vorbereitet...</label>
                <progress id="kern-feedback-progress-bar" value="0" max="100"></progress>
              </div>
            </div>
            <div class="kern-feedback-screenshot__status" id="kern-feedback-screenshot-status" style="display: none;"></div>
            <div class="kern-feedback-screenshot__separator" id="kern-feedback-screenshot-separator">
              <span class="kern-body kern-body--small">oder</span>
            </div>
            <div class="kern-form-input kern-feedback-file-upload" id="kern-feedback-file-wrapper">
              <label class="kern-label" for="kern-feedback-file-upload">Bild hochladen
                <span class="kern-label__optional">- Optional</span>
              </label>
              <div class="kern-hint" id="kern-feedback-file-hint">Erlaubte Formate: ${getAllowedFormatsText()}. Maximale Dateigröße: 8 MB.</div>
              <input class="kern-form-input__input"
                     id="kern-feedback-file-upload"
                     name="screenshotFile"
                     type="file"
                     accept="${getFileInputAcceptAttribute()}"
                     aria-describedby="kern-feedback-file-hint">
            </div>
            <div class="kern-feedback-screenshot__preview" id="kern-feedback-preview" style="display: none;">
              <img src="" alt="Screenshot Vorschau" id="kern-feedback-preview-img">
              <button type="button"
                      class="kern-feedback-screenshot__remove"
                      id="kern-feedback-remove-screenshot"
                      aria-label="Screenshot entfernen">
                ${ICONS.close}
              </button>
            </div>
          </div>
        </div>
      `
      : '';

    return `
      <header class="kern-dialog__header">
        <h2 class="kern-title kern-title--large" id="kern-feedback-title">
          ${this.config.dialogTitle}
        </h2>
        <button type="button"
                class="kern-btn kern-btn--tertiary"
                aria-label="Dialog schließen"
                id="kern-feedback-close">
          <span class="kern-icon kern-icon--close" aria-hidden="true">${ICONS.close}</span>
          <span class="kern-sr-only">Schließen</span>
        </button>
      </header>

      <section class="kern-dialog__body" id="kern-feedback-form">
        ${ratingSection}

        <div class="kern-form-input">
          <label class="kern-label" for="kern-feedback-comment">
            Dein Feedback
          </label>
          <textarea class="kern-form-input__input"
                    id="kern-feedback-comment"
                    name="comment"
                    required
                    rows="5"></textarea>
          <p class="kern-error" id="kern-feedback-comment-error" role="alert" style="display: none;">
            <span class="kern-icon kern-icon--danger kern-icon--md" aria-hidden="true"></span>
            <span class="kern-body">Bitte gib einen Kommentar ein.</span>
          </p>
        </div>

        ${emailSection}
        ${screenshotSection}
      </section>

      <div class="kern-form-input kern-feedback-spacer"><p><span>&nbsp;</span></p></div>

      <footer class="kern-dialog__footer">
        <button type="button" class="kern-btn kern-btn--secondary" id="kern-feedback-cancel">
          <span class="kern-label">Abbrechen</span>
        </button>
        <button type="submit" class="kern-btn kern-btn--primary" id="kern-feedback-submit">
          <span class="kern-label" id="kern-feedback-submit-text">Absenden</span>
        </button>
      </footer>

      ${
        this.isSafariBrowser()
          ? '<div class="kern-form-input kern-feedback-spacer"><p><span>&nbsp;</span></p></div>'
          : ''
      }

      <div class="kern-feedback-success" id="kern-feedback-success">
        <div class="kern-feedback-success__icon">${ICONS.success}</div>
        <h3 class="kern-title kern-title--large">Vielen Dank!</h3>
        <p class="kern-body">Dein Feedback wurde erfolgreich übermittelt.</p>
      </div>
    `;
  }

  private setupDialogEvents(): void {
    if (!this.dialog) return;

    // Schließen-Button
    this.dialog
      .querySelector('#kern-feedback-close')
      ?.addEventListener('click', () => this.close());
    this.dialog
      .querySelector('#kern-feedback-cancel')
      ?.addEventListener('click', () => this.close());

    // Escape-Taste — keep a reference so the listener can be removed in
    // destroy() and never leaks onto `document` across widget lifecycles.
    if (!this.escapeKeyHandler) {
      this.escapeKeyHandler = (e: KeyboardEvent): void => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      };
      document.addEventListener('keydown', this.escapeKeyHandler);
    }

    // Star Rating
    if (this.config.showRating) {
      this.setupRating();
    }

    // Screenshot
    if (this.config.enableScreenshot) {
      this.setupScreenshot();
    }

    // Form Submit
    this.dialog.querySelector('#kern-feedback-submit')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.submit();
    });
  }

  private setupRating(): void {
    const stars = this.dialog?.querySelectorAll('.kern-feedback-rating__star');
    const label = this.dialog?.querySelector('#rating-label');
    const labels = ['', 'Mangelhaft', 'Ausreichend', 'Befriedigend', 'Gut', 'Sehr gut'];

    stars?.forEach((star) => {
      star.addEventListener('mouseenter', () => {
        const rating = parseInt(star.getAttribute('data-rating') || '0', 10);
        this.highlightStars(rating, true);
      });

      star.addEventListener('mouseleave', () => {
        this.highlightStars(this.currentRating, false);
      });

      star.addEventListener('click', () => {
        const rating = parseInt(star.getAttribute('data-rating') || '0', 10);
        this.currentRating = rating;
        this.highlightStars(rating, false);
        if (label) {
          label.textContent = labels[rating] || '';
        }

        // ARIA aktualisieren
        stars.forEach((s) => {
          s.setAttribute('aria-checked', 'false');
        });
        star.setAttribute('aria-checked', 'true');
      });
    });
  }

  private highlightStars(rating: number, isHover: boolean): void {
    const stars = this.dialog?.querySelectorAll('.kern-feedback-rating__star');
    stars?.forEach((star, index) => {
      const isFilled = index < rating;
      star.innerHTML = isFilled ? ICONS.star : ICONS.starOutline;

      if (isHover) {
        star.classList.toggle('kern-feedback-rating__star--hover', isFilled);
      } else {
        star.classList.remove('kern-feedback-rating__star--hover');
        star.classList.toggle('kern-feedback-rating__star--active', isFilled);
      }
    });
  }

  private setupScreenshot(): void {
    const preview = this.dialog?.querySelector('#kern-feedback-preview') as HTMLElement;
    const removeBtn = this.dialog?.querySelector(
      '#kern-feedback-remove-screenshot'
    ) as Element | null;

    // Always wire the file upload if a file input is present
    // (mobile: the only option; desktop: an alternative to native capture)
    const fileInput = this.dialog?.querySelector(
      '#kern-feedback-file-upload'
    ) as HTMLInputElement | null;
    if (fileInput) {
      this.setupFileUpload(preview, removeBtn);
    }

    // On mobile devices (or when simulateMobile is enabled), only file upload is used
    if (this.isMobileBehavior()) {
      return;
    }

    const captureBtn = this.dialog?.querySelector('#kern-feedback-capture') as HTMLButtonElement;

    if (!captureBtn) {
      console.warn('KERN Feedback: Screenshot button not found');
      return;
    }

    // Handle attach/capture button click
    captureBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      this.userWantsScreenshot = true;

      const currentState = this.screenshotManager.getState();

      if (currentState === 'completed') {
        // Screenshot already ready - just show it
        const result = this.screenshotManager.getResult();
        if (result) {
          this.screenshot = result;
          this.showScreenshotPreview(result);
        }
      } else if (currentState === 'capturing') {
        // Still capturing - show waiting state and wait for completion
        captureBtn.disabled = true;
        captureBtn.innerHTML = `<span class="kern-feedback-loader"></span><span class="kern-label">Wird erstellt...</span>`;

        try {
          const result = await this.screenshotManager.waitForResult();
          this.screenshot = result;
          this.showScreenshotPreview(result);
        } catch (error) {
          console.error('Screenshot capture failed:', error);
          this.config.onError?.(error as Error);
          this.userWantsScreenshot = false;
          // Reset button state
          captureBtn.disabled = false;
          captureBtn.innerHTML = `<span class="kern-icon kern-icon--content-copy" aria-hidden="true"></span><span class="kern-label">Erneut versuchen</span>`;
        }
      } else if (
        currentState === 'failed' ||
        currentState === 'idle' ||
        currentState === 'cancelled'
      ) {
        // Need to start new capture
        captureBtn.disabled = true;
        captureBtn.innerHTML = `<span class="kern-feedback-loader"></span><span class="kern-label">Wird erstellt...</span>`;

        // Reset and start new capture
        this.screenshotManager.reset();

        // Re-subscribe to state changes
        if (this.screenshotStateUnsubscribe) {
          this.screenshotStateUnsubscribe();
        }
        this.screenshotStateUnsubscribe = this.screenshotManager.onStateChange(
          (state, result, error) => this.handleScreenshotStateChange(state, result, error)
        );

        // Hide dialog and backdrop before capturing (for native method)
        // so they don't appear in the screenshot
        const isNativeMethod = this.config.screenshotMethod === 'native';
        if (isNativeMethod) {
          this.hideWidgetForScreenshot();
        }

        try {
          this.screenshotManager.startCapture();
          const result = await this.screenshotManager.waitForResult();

          // Show dialog again after capture
          if (isNativeMethod) {
            this.showWidgetAfterScreenshot();
          }

          this.screenshot = result;
          this.showScreenshotPreview(result);
        } catch (error) {
          // Show dialog again even on error
          if (isNativeMethod) {
            this.showWidgetAfterScreenshot();
          }

          console.error('Screenshot capture failed:', error);
          this.config.onError?.(error as Error);
          this.userWantsScreenshot = false;
          captureBtn.disabled = false;
          captureBtn.innerHTML = `<span class="kern-icon kern-icon--content-copy" aria-hidden="true"></span><span class="kern-label">Erneut versuchen</span>`;
        }
      }
    });

    // Handle remove screenshot button (desktop capture branch).
    // Remove deletes any captured screenshot so that the next click on
    // "Screenshot erstellen" triggers a fresh capture, and re-shows the
    // capture button (plus the file-upload alternative if present).
    removeBtn?.addEventListener('click', () => {
      this.screenshot = null;
      this.userWantsScreenshot = false;

      // Discard any captured screenshot in the manager so the next click
      // on the capture button starts a brand-new capture instead of
      // re-attaching the previous one.
      this.screenshotManager.reset();

      if (preview) {
        preview.style.display = 'none';
      }

      // Show capture button again. After the manager reset above the state
      // is 'idle', so the button label will read "Screenshot erstellen".
      if (captureBtn) {
        captureBtn.style.display = '';
        this.updateScreenshotUI(this.screenshotManager.getState());
      }

      // Restore the desktop file-upload alternative if present
      const fileWrapper = this.dialog?.querySelector(
        '#kern-feedback-file-wrapper'
      ) as HTMLElement | null;
      const separator = this.dialog?.querySelector(
        '#kern-feedback-screenshot-separator'
      ) as HTMLElement | null;
      if (fileWrapper) {
        fileWrapper.style.display = '';
      }
      if (separator) {
        separator.style.display = '';
      }
    });
  }

  /**
   * Set up file upload for screenshots.
   * On mobile (iOS/Android) this is the only way to attach a screenshot
   * because the Screen Capture API is not available.
   * On desktop this is offered as an alternative to the native capture button.
   */
  private setupFileUpload(preview: HTMLElement | null, removeBtn: Element | null): void {
    const fileInput = this.dialog?.querySelector('#kern-feedback-file-upload') as HTMLInputElement;

    if (!fileInput) {
      console.warn('KERN Feedback: File upload input not found');
      return;
    }

    // Handle file selection
    fileInput.addEventListener('change', async () => {
      const file = fileInput.files?.[0];
      if (!file) return;

      // Quick MIME type check (first line of defense, but not sufficient alone)
      const allowedTypes = getAllowedMimeTypes();
      if (!allowedTypes.includes(file.type)) {
        alert(`Bitte wähle eine Bilddatei (${getAllowedFormatsText()}).`);
        fileInput.value = '';
        return;
      }

      // Validate file size (max 8MB)
      const maxSize = 8 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('Die Datei ist zu groß. Maximale Größe: 8 MB.');
        fileInput.value = '';
        return;
      }

      try {
        // Security: Validate file format by checking magic bytes and structure
        // This prevents malicious files disguised as images (e.g., SVG with scripts)
        const validationResult = await validateImageFile(file);

        if (!validationResult.valid) {
          alert(validationResult.error || 'Die Datei konnte nicht validiert werden.');
          fileInput.value = '';
          return;
        }

        // Read file as data URL (only after validation passes)
        const dataUrl = await this.readFileAsDataUrl(file);

        // Get image dimensions
        const dimensions = await this.getImageDimensions(dataUrl);

        // Create screenshot result
        this.screenshot = {
          blob: file,
          dataUrl,
          width: dimensions.width,
          height: dimensions.height,
        };

        // Mark that the user has an attached screenshot so that a background
        // native capture completing later does NOT clobber the uploaded file
        // via handleScreenshotStateChange.
        this.userWantsScreenshot = true;

        // Show preview using the centralized helper. This also hides the
        // file input, file-wrapper, capture button, status and progress.
        this.showScreenshotPreview(this.screenshot);

        // Keep the original-source preview view (the helper handles inputs)
        if (preview) {
          const previewImg = preview.querySelector(
            '#kern-feedback-preview-img'
          ) as HTMLImageElement;
          if (previewImg && !previewImg.src) {
            previewImg.src = dataUrl;
          }
        }
      } catch (error) {
        console.error('Error processing file:', error);
        alert('Fehler beim Verarbeiten der Datei. Bitte versuche es erneut.');
        fileInput.value = '';
      }
    });

    // Handle remove screenshot button
    removeBtn?.addEventListener('click', () => {
      this.screenshot = null;
      this.userWantsScreenshot = false;

      if (preview) {
        preview.style.display = 'none';
        const previewImg = preview.querySelector('#kern-feedback-preview-img') as HTMLImageElement;
        if (previewImg) {
          previewImg.src = '';
        }
      }

      // Restore the file-upload wrapper and input so the user can pick again
      const fileWrapper = this.dialog?.querySelector(
        '#kern-feedback-file-wrapper'
      ) as HTMLElement | null;
      const separator = this.dialog?.querySelector(
        '#kern-feedback-screenshot-separator'
      ) as HTMLElement | null;
      if (fileWrapper) {
        fileWrapper.style.display = '';
      }
      if (separator) {
        separator.style.display = '';
      }
      if (fileInput) {
        fileInput.style.display = '';
        fileInput.value = '';
      }
    });
  }

  /**
   * Read a file as a data URL
   */
  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get dimensions of an image from its data URL
   */
  private getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  }

  private async submit(): Promise<void> {
    if (this.isSubmitting) return;

    const commentInput = this.dialog?.querySelector(
      '#kern-feedback-comment'
    ) as HTMLTextAreaElement;
    const emailInput = this.dialog?.querySelector(
      '#kern-feedback-email'
    ) as HTMLInputElement | null;
    const emailField = this.dialog?.querySelector(
      '#kern-feedback-email-field'
    ) as HTMLElement | null;
    const emailError = this.dialog?.querySelector(
      '#kern-feedback-email-error'
    ) as HTMLElement | null;
    const submitBtn = this.dialog?.querySelector('#kern-feedback-submit') as HTMLButtonElement;
    const submitText = this.dialog?.querySelector('#kern-feedback-submit-text') as HTMLElement;
    const commentError = this.dialog?.querySelector('#kern-feedback-comment-error') as HTMLElement;

    // Validierung
    const comment = commentInput?.value.trim();
    if (!comment) {
      commentInput?.classList.add('kern-form-input__input--error');
      commentInput?.parentElement?.classList.add('kern-form-input--error');
      if (commentError) commentError.style.display = 'flex';
      commentInput?.focus();
      return;
    }

    commentInput?.classList.remove('kern-form-input__input--error');
    commentInput?.parentElement?.classList.remove('kern-form-input--error');
    if (commentError) commentError.style.display = 'none';

    // E-Mail-Validierung (nur wenn ausgefüllt)
    const emailValue = emailInput?.value.trim() || '';
    if (emailValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailValue)) {
        emailInput?.classList.add('kern-form-input__input--error');
        emailField?.classList.add('kern-form-input--error');
        if (emailError) emailError.style.display = 'flex';
        emailInput?.focus();
        return;
      }
    }

    // E-Mail-Fehler zurücksetzen
    emailInput?.classList.remove('kern-form-input__input--error');
    emailField?.classList.remove('kern-form-input--error');
    if (emailError) emailError.style.display = 'none';

    this.isSubmitting = true;
    submitBtn.disabled = true;
    submitText.innerHTML = `<span class="kern-feedback-loader"></span> Wird gesendet...`;

    try {
      // Browser-Info als JSON für zusätzliche Metadaten
      const browserInfo = {
        page_title: document.title,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        referrer: document.referrer || undefined,
        browser_language: navigator.language,
      };

      const formData = new FormData();
      formData.append('message', comment);
      formData.append('url', window.location.href);
      formData.append('user_agent', navigator.userAgent);
      formData.append('browser_info', JSON.stringify(browserInfo));

      if (emailInput?.value.trim()) {
        formData.append('email', emailInput.value.trim());
      }
      if (this.currentRating) {
        formData.append('rating', String(this.currentRating));
      }

      // Screenshot anhängen
      if (this.screenshot) {
        formData.append('screenshot', this.screenshot.blob, 'screenshot.png');
      }

      const abortController = new AbortController();
      this.submitAbortController = abortController;

      const response = await fetch(`${this.config.apiUrl}/feedback`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
        },
        body: formData,
        signal: abortController.signal,
      });

      const result: FeedbackSubmitResult = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Feedback konnte nicht gesendet werden');
      }

      // Erfolg anzeigen
      this.showSuccess();
      this.config.onSubmit?.(result);

      // Nach 2 Sekunden schließen
      this.schedule(() => {
        this.close();
        this.hideSuccess();
      }, 2000);
    } catch (error) {
      // A destroy() while the request was in flight aborts it on purpose:
      // the widget is gone, so neither the error callback nor the alert
      // (which would outlive the widget) may fire.
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Feedback-Fehler:', error);
      this.config.onError?.(error as Error);
      alert('Feedback konnte nicht gesendet werden. Bitte versuche es später erneut.');
    } finally {
      this.submitAbortController = null;
      this.isSubmitting = false;
      submitBtn.disabled = false;
      submitText.textContent = 'Absenden';
    }
  }

  private showSuccess(): void {
    const header = this.dialog?.querySelector('.kern-dialog__header') as HTMLElement;
    const form = this.dialog?.querySelector('#kern-feedback-form') as HTMLElement;
    const footer = this.dialog?.querySelector('.kern-dialog__footer') as HTMLElement;
    const success = this.dialog?.querySelector('#kern-feedback-success') as HTMLElement;
    const spacers = this.dialog?.querySelectorAll('.kern-feedback-spacer');

    if (header) header.classList.add('kern-dialog__header--hidden');
    if (form) form.classList.add('kern-dialog__body--hidden');
    if (footer) footer.classList.add('kern-dialog__footer--hidden');
    if (spacers) spacers.forEach((s) => s.classList.add('kern-feedback-spacer--hidden'));
    if (success) success.classList.add('kern-feedback-success--visible');
  }

  private hideSuccess(): void {
    const header = this.dialog?.querySelector('.kern-dialog__header') as HTMLElement;
    const form = this.dialog?.querySelector('#kern-feedback-form') as HTMLElement;
    const footer = this.dialog?.querySelector('.kern-dialog__footer') as HTMLElement;
    const success = this.dialog?.querySelector('#kern-feedback-success') as HTMLElement;
    const spacers = this.dialog?.querySelectorAll('.kern-feedback-spacer');

    if (header) header.classList.remove('kern-dialog__header--hidden');
    if (form) form.classList.remove('kern-dialog__body--hidden');
    if (footer) footer.classList.remove('kern-dialog__footer--hidden');
    if (spacers) spacers.forEach((s) => s.classList.remove('kern-feedback-spacer--hidden'));
    if (success) success.classList.remove('kern-feedback-success--visible');
  }

  private resetForm(): void {
    const commentInput = this.dialog?.querySelector(
      '#kern-feedback-comment'
    ) as HTMLTextAreaElement;
    const emailInput = this.dialog?.querySelector(
      '#kern-feedback-email'
    ) as HTMLInputElement | null;
    const emailField = this.dialog?.querySelector(
      '#kern-feedback-email-field'
    ) as HTMLElement | null;
    const emailError = this.dialog?.querySelector(
      '#kern-feedback-email-error'
    ) as HTMLElement | null;
    const preview = this.dialog?.querySelector('#kern-feedback-preview') as HTMLElement;
    const commentError = this.dialog?.querySelector('#kern-feedback-comment-error') as HTMLElement;
    const captureBtn = this.dialog?.querySelector('#kern-feedback-capture') as HTMLButtonElement;
    const fileInput = this.dialog?.querySelector('#kern-feedback-file-upload') as HTMLInputElement;
    const statusIndicator = this.dialog?.querySelector(
      '#kern-feedback-screenshot-status'
    ) as HTMLElement;
    const progressContainer = this.dialog?.querySelector(
      '#kern-feedback-screenshot-progress'
    ) as HTMLElement;

    if (commentInput) {
      commentInput.value = '';
      commentInput.classList.remove('kern-form-input__input--error');
      commentInput.parentElement?.classList.remove('kern-form-input--error');
    }
    if (emailInput) {
      emailInput.value = '';
      emailInput.classList.remove('kern-form-input__input--error');
    }
    if (emailField) emailField.classList.remove('kern-form-input--error');
    if (emailError) emailError.style.display = 'none';
    if (preview) preview.style.display = 'none';
    if (commentError) commentError.style.display = 'none';

    // Reset screenshot UI elements
    if (captureBtn) {
      captureBtn.style.display = '';
      captureBtn.disabled = false;
      captureBtn.innerHTML = `<span class="kern-icon kern-icon--content-copy" aria-hidden="true"></span><span class="kern-label">Screenshot erstellen & anhängen</span>`;
    }
    if (statusIndicator) {
      statusIndicator.style.display = 'none';
    }
    if (progressContainer) {
      progressContainer.style.display = 'none';
    }

    // Reset file input for mobile devices
    if (fileInput) {
      fileInput.style.display = '';
      fileInput.value = '';
    }

    // Restore the desktop file-upload wrapper and separator (may have been
    // hidden when a screenshot was attached either via capture or upload)
    const fileWrapper = this.dialog?.querySelector(
      '#kern-feedback-file-wrapper'
    ) as HTMLElement | null;
    const separator = this.dialog?.querySelector(
      '#kern-feedback-screenshot-separator'
    ) as HTMLElement | null;
    if (fileWrapper) {
      fileWrapper.style.display = '';
    }
    if (separator) {
      separator.style.display = '';
    }

    // Clear preview image
    const previewImg = this.dialog?.querySelector(
      '#kern-feedback-preview-img'
    ) as HTMLImageElement | null;
    if (previewImg) {
      previewImg.src = '';
    }

    this.currentRating = 0;
    this.screenshot = null;
    this.userWantsScreenshot = false;
    this.highlightStars(0, false);

    const label = this.dialog?.querySelector('#rating-label');
    if (label) label.textContent = '';

    this.hideSuccess();
  }
}
